import { type Entries, InstantieId, type Person, type ViewMode } from '@eformulieren/model'
import { type PrefillStorage, takeStashedPrefill } from './prefillStash.js'

/**
 * De context van de pagina: wie er kijkt, welke instantie het is en wat de link meedraagt (R-154,
 * R-203, R-37). Tot deze opsplitsing stond dit bovenaan `FormPage.tsx`; zie de kop daar voor waarom
 * het token niet in de URL staat.
 */

export interface FormContext {
  /**
   * Het OIDC-token van de invuller (R-154), of niets bij een directe link.
   *
   * **Twee wegen naar binnen, en de gastheer wint.** Ingebed bij een gastheer geeft die het
   * token mee en gaat het in een header; op een directe link is er geen gastheer en heeft de
   * browser het sessiecookie dat de inlogdienst heeft gezet. De pod kent die twee wegen allebei
   * en laat de header voorgaan (`server.ts`), en de samenwerklaag doet hetzelfde bij de
   * handshake (`samenwerken.ts`).
   *
   * Tot M5.16c stond hij hier verplicht, en daarmee stuurde deze pagina altijd een header, ook
   * als er niets in zat: de letterlijke tekst `Bearer undefined`. Die wint van het cookie, dus de
   * directe link was voor de enige pagina die er is onbereikbaar. Dat viel op bij de eerste echte
   * inlog op het cluster. Zie `headersFor` in `podRequests.ts` voor waarom het `Bearer undefined` is en niet
   * een lege `Bearer`; dat stond hier tot M5.16f verkeerd.
   */
  readonly token?: string
  /**
   * Wie er handelt.
   *
   * De projectie draagt hem niet, en dat is juist: de pod stempelt de herkomst zelf
   * uit het token en gelooft de client daarin nooit (R-154). Wat het scherm nodig
   * heeft is alleen een naam om naast een wijziging te zetten.
   *
   * **Twee wegen ernaartoe, en dit veld is altijd al gevuld tegen de tijd dat het hier
   * staat.** Met de hand gezet in een proef of een e2e-flow komt hij, net als voorheen, uit
   * `window.__proef` of `sessionStorage`. Op een echte directe link, zonder een van beide,
   * vult `main.tsx` hem aan met `GET /whoami` (`leesendpoints.ts`) voordat deze context
   * ontstaat: dat endpoint vertaalt het HttpOnly-sessiecookie naar precies dezelfde
   * `persoon` die de pod ook voor elke schrijfactie gebruikt. Dat was tot deze opdracht de
   * open vraag van M5.16d; zie `contextstand.ts` voor de afweging (wachten, niet meteen
   * "geen context") en `main.tsx` voor de orkestratie.
   */
  readonly persoon: Person
  /**
   * Welke instantie dit is (R-203).
   *
   * Sinds M8.1 wint het pad van de link (`/ins-…`) hierover, bij het opkomen van de pagina: als
   * het pad daar een geldig instantie-id bevat, overschrijft `leesContext()` deze waarde ermee.
   * Deze waarde is dan alleen nog de terugval als het pad geen match geeft, zoals op het
   * wortelpad van een dev-proef (`instantieFromPath`, `leesContext`). Een sprong binnen een reeks
   * (`onNavigate` in `FormPage`) zet dit veld daarna zelf via `setInstantieId`, zonder de URL
   * bij te werken; dat is bestaand gedrag van vóór M8.1 en hier ongewijzigd gelaten.
   */
  readonly instantieId: string
  /**
   * Een expliciete gedaante, of niets (D-25, D-61, R-138a).
   *
   * **Zonder deze waarde volgt de gedaante uit de instantie en uit wie er kijkt**
   * (`gedaante.ts`). Sinds M9 is elke beurt een eigen instantie: het processysteem kiest de
   * gedaante door de instantie aan te maken (een opvolger in de toetslaag, een bevroren beurt,
   * een opvolger na terugleggen), dus de pagina leest hem en hoeft hem niet te krijgen. Tot deze
   * opdracht stond hier dat zelf afleiden "beleid van het processysteem" zou zijn en dat de weg
   * op een directe link nog niet besloten was; dat was van vóór M9, toen één instantie door alle
   * beurten heen liep. Zie `docs/dekking.md`, "De gedaante volgt uit de instantie".
   *
   * **Staat hij er wel, dan wint hij**, uit `window.__proef` of `sessionStorage`: een proef en de
   * e2e-flows zetten zo een gedaante die de instantie niet zou geven. De gedaante is geen recht;
   * de pod bewaakt elke schrijfactie zelf.
   */
  readonly modus?: ViewMode
  /**
   * De reeks waarin dit dossier staat, in volgorde, of niets (R-122, T2).
   *
   * De instanties van de werkvoorraad, zoals het processysteem ze op een rij zet; deze pagina
   * rekent er zijn positie uit en verzint de volgorde niet. Buiten een werkvoorraad is er geen
   * reeks en staat er geen navigator; staat de eigen instantie er niet in, dan ook niet, want
   * dan zegt de aanlevering iets anders dan bedoeld.
   *
   * **De lijst en niet alleen de buren.** Deze docstring zei tot M8.1 nog "vanaf M8 komt de
   * positie per sprong over postMessage binnen (B8)"; die naad bestaat niet meer sinds besluit A.
   * Anders dan `modus` (hierboven) volgt de reeks niet uit de instantie: een werkvoorraad is iets
   * van het processysteem en staat op geen enkele instantie. Hoe een processysteem
   * de reeks op een echte directe link aanlevert is nog niet besloten, en zolang dat zo is heeft
   * de navigator de hele lijst nodig (niet alleen de buren) om ook na een tweede sprong nog te
   * weten waar hij staat. De navigator zelf merkt het verschil niet: die krijgt zijn positie en
   * zijn buren, langs welke weg dan ook.
   */
  readonly reeks?: readonly string[]
  /**
   * Kandidaat-waarden uit de querystring, per veld-id (R-37, M8.6).
   *
   * Altijd berekend en nooit van de gastheer: anders dan `instantieId` heeft dit veld geen
   * aangeleverde basis om over heen te winnen, het bestaat uitsluitend uit wat de link zelf
   * meedraagt. `Invulformulier` beslist welk veld, zo nodig geen enkel, ervoor in aanmerking
   * komt; deze pagina raadt niets over veldtypes.
   */
  readonly voorvulling: Readonly<Record<string, string>>
  /**
   * Wanneer de sessie verloopt, in seconden sinds 1970 (`exp` van het token, uit `GET /whoami`),
   * of niets als dat niet bekend is. Voor de waarschuwing vooraf (WCAG 2.2.1).
   */
  readonly verlooptOp?: number
  /**
   * De eigen ingang uit `GET /whoami`, in de vorm van `Ingangen` met die ene ingang, of niets. Daarmee
   * biedt de waarschuwing "Sessie verlengen" ook als `/ingangen` niet te lezen was (WCAG 2.2.1, R-139):
   * hij komt mee met de vervaltijd, dus waar een waarschuwing is, is ook een adres om te verlengen.
   */
  readonly ingang?: Entries
}

declare global {
  interface Window {
    __proef?: FormContext
  }
}

function fromSessionStorage(): FormContext | undefined {
  const saved = window.sessionStorage.getItem('proef')
  if (saved === null) return undefined
  try {
    return JSON.parse(saved) as FormContext
  } catch {
    // Een onleesbare context is geen reden om iets te verzinnen: de pagina zegt dan dat de
    // gastheer hem moet aanleveren, en dat is de begrijpelijke uitkomst.
    return undefined
  }
}

/**
 * De instantie zoals hij in het pad van de link staat (`/ins-…`, koppelvlak.md hoofdstuk 4), of
 * geen als het pad geen instantie-id is.
 *
 * Op `InstantieId` uit `packages/model` getoetst en niet met de hand herschreven: dat is de
 * enige plek waar de vorm van dit id staat (terminologieregel, `id.ts`).
 *
 * Geëxporteerd, en niet alleen voor `leesContext()` hieronder: `main.tsx` roept hem ook aan
 * vóórdat er een basis is, om te weten of een ontbrekende `persoon` reden is om op `/whoami`
 * te wachten of meteen "geen context" te tonen (`contextstand.ts`).
 */
export function instantieFromPath(): string | undefined {
  // Zowel de kop- als de staartslash eraf: een link met een sluitende `/` bestaat
  // (met de hand getypt, of gekopieerd met een trailing slash) en moet hetzelfde werken.
  const segment = window.location.pathname.replace(/^\/+|\/+$/g, '')
  return InstantieId.safeParse(segment).success ? segment : undefined
}

/**
 * Elke parameter uit de querystring, per naam (R-37, M8.6).
 *
 * Deze pagina raadt niet welke naam bij welk veld hoort: de conventie is dat de naam van de
 * parameter gelijk is aan het veld-id, en de renderer beslist zelf welk veld ervoor in
 * aanmerking komt (alleen leeg, alleen `text`/`radio`/`dropdown`, alleen een bekende optie).
 * Een naam die bij geen enkel veld hoort wordt daar dus stilzwijgend genegeerd; dat is dezelfde
 * val-terug als een portaal dat een trackingparameter meestuurt.
 *
 * Geëxporteerd zodat `main.tsx` dezelfde voorvulling kan gebruiken bij een context die pas na
 * `/whoami` compleet wordt: die weg loopt niet via `leesContext()`, maar de voorvulling komt uit
 * dezelfde plek (`contextstand.ts`).
 */
export function prefillFromQuerystring(): Record<string, string> {
  return { ...stashedOnArrival(), ...Object.fromEntries(new URLSearchParams(window.location.search).entries()) }
}

/**
 * De `sessionStorage` van dit tabblad, of een opslag die niets bewaart als de browser hem weigert
 * (geblokkeerde sitegegevens): dan werkt de pagina, alleen overleeft een lange voorvulling het
 * inloggen niet.
 */
export function tabStorage(): PrefillStorage {
  try {
    return window.sessionStorage
  } catch {
    return { getItem: () => null, setItem: () => undefined, removeItem: () => undefined }
  }
}

let arrival: Readonly<Record<string, string>> | undefined

/**
 * Een voorvulling die te lang was voor het doel van het inloggen en daarom in dit tabblad wachtte
 * (`prefillStash.ts`), één keer per paginalading gelezen en daarmee gewist. De query wint bij een
 * dubbele naam: wat de link zelf draagt is het nieuwste.
 */
function stashedOnArrival(): Readonly<Record<string, string>> {
  arrival ??= takeStashedPrefill(tabStorage(), instantieFromPath(), Date.now())
  return arrival
}

/**
 * De context uit `window` of uit `sessionStorage`, met het pad erover heen voor de instantie.
 *
 * **Het pad wint, waar het meedoet, en de rest komt nog altijd ergens anders vandaan.** M8.1
 * lost hier de vraag op welke instantie het is: die staat nu in de link zelf (B19 geeft straks
 * precies zo'n link terug) en hoeft niet meer te worden meegespeeld.
 *
 * **Wie er kijkt (`persoon`) blijft hier ongewijzigd uit `window.__proef` of `sessionStorage`
 * komen, en dat is met opzet.** Deze functie is en blijft synchroon: ze doet geen netwerkverzoek
 * en geeft `undefined` terug zonder basis, precies zoals vóór deze opdracht. Wat nieuw is staat
 * een laag hoger, in `main.tsx`: ontbreekt de basis maar staat er wel een instantie in het pad,
 * dan wacht de pagina daar op `GET /whoami` in plaats van meteen "geen context" te tonen
 * (`contextstand.ts`, `beginContextstand`). Roept iemand alleen déze functie aan (zoals de
 * bestaande e2e-opstellingen die `window.__proef` met de hand zetten), dan verandert er niets.
 */
export function readContext(): FormContext | undefined {
  const base = window.__proef ?? fromSessionStorage()
  if (base === undefined) return undefined
  const fromPath = instantieFromPath()
  return {
    ...base,
    ...(fromPath === undefined ? {} : { instantieId: fromPath }),
    voorvulling: prefillFromQuerystring(),
  }
}
